import React from 'react';
import ReactECharts from 'echarts-for-react';

const PortfolioChart = ({ portfolio, data, width = 300, height = 300 }) => {
  let pieData = [];
  
  // Handle different data formats
  if (data && Array.isArray(data)) {
    // Direct data format: [{name, value, color}, ...]
    pieData = data.map(item => ({
      name: item.name,
      value: item.value,
      itemStyle: { color: item.color }
    }));
  } else if (portfolio && portfolio.holdings && portfolio.holdings.length > 0) {
    // Portfolio holdings format
    const assetClassTotals = {};
    portfolio.holdings.forEach(holding => {
      const assetClass = holding.asset_class || 'Other';
      assetClassTotals[assetClass] = (assetClassTotals[assetClass] || 0) + (holding.market_value || 0);
    });
    
    pieData = Object.entries(assetClassTotals).map(([name, value]) => ({
      name,
      value: Math.round(value)
    }));
  } else {
    return (
      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-600">Portfolio visualization will appear here</p>
        <p className="text-xs text-gray-400 mt-1">Based on risk profile recommendations</p>
      </div>
    );
  }

  // VAIA IB Platform brand colors (ugmonk palette)
  const sanctumColors = [
    '#ffd87a',  // Yellow — primary
    '#ff3451',  // Red
    '#d9e2c3',  // Sage
    '#1a1a1a',  // Dark
    '#a8a8a8',  // Grey
    '#f5cc6a',  // Yellow hover
    '#2a2a2a',  // Darker
    '#e8293f',  // Red hover
  ];

  const pieOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const percentage = params.percent;
        const name = params.name;
        const value = params.value;
        
        // Format based on data type
        if (typeof value === 'number' && value > 1000) {
          return `${name}<br/>₹${value.toLocaleString('en-IN')} (${percentage}%)`;
        } else {
          return `${name}<br/>${percentage}%`;
        }
      },
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: '#ffd87a',
      borderWidth: 1,
      textStyle: { 
        color: '#1f2937',
        fontSize: 12,
        fontWeight: '500'
      },
      padding: [8, 12]
    },
    legend: {
      orient: 'horizontal',
      bottom: '8%',
      left: 'center',
      textStyle: { 
        color: '#374151', 
        fontSize: 11,
        fontWeight: '500'
      },
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 25,
      padding: [10, 0, 0, 0],
      formatter: (name) => {
        // Shorten long names for horizontal layout
        if (name.length > 12) {
          return name.substring(0, 10) + '...';
        }
        return name;
      }
    },
    series: [
      {
        name: 'Portfolio Allocation',
        type: 'pie',
        radius: ['40%', '65%'],
        center: ['50%', '40%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 6,
          borderColor: '#ffffff',
          borderWidth: 2,
          shadowBlur: 4,
          shadowColor: 'rgba(0, 0, 0, 0.1)'
        },
        label: {
          show: true,
          position: 'inside',
          fontSize: 12,
          fontWeight: 'bold',
          color: '#ffffff',
          formatter: '{d}%'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold',
            color: '#ffffff',
            formatter: '{d}%'
          },
          itemStyle: {
            shadowBlur: 8,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
            borderWidth: 3
          }
        },
        labelLine: {
          show: false
        },
        data: pieData.map((item, index) => ({
          ...item,
          itemStyle: {
            color: data ? item.itemStyle?.color : sanctumColors[index % sanctumColors.length],
            borderRadius: 6,
            borderColor: '#ffffff',
            borderWidth: 2,
            shadowBlur: 4,
            shadowColor: 'rgba(0, 0, 0, 0.1)'
          }
        })),
        animationType: 'expansion',
        animationDuration: 1000,
        animationEasing: 'cubicOut'
      }
    ]
  };

  return (
    <div className="w-full h-full bg-white rounded-lg overflow-hidden">
      <ReactECharts 
        option={pieOption} 
        style={{ 
          width: '100%', 
          height: `${height}px`,
          maxWidth: `${width}px`,
          margin: '0 auto'
        }} 
        opts={{ renderer: 'svg' }}
        notMerge={true}
        lazyUpdate={true}
      />
    </div>
  );
};

export default PortfolioChart;